import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { RolloverDraft } from './studyYearApi';
import { formatShortDate } from './studyTermProgress';
import {
  MAX_NEXT_TERM_SUBJECTS,
  MAX_YEAR_LEVEL,
  MIN_YEAR_LEVEL,
  REFLECTION_MAX,
  WRAP_UP_STEPS,
  addableSubjects,
  catalogueSubjectName,
  isYearWrapUp,
  nextTermSubjectCount,
  nextPeriodLabel,
  unfinishedSentence,
  type AssessmentChoice,
  type TaskChoice,
  type WrapUpState,
  type WrapUpStep,
} from './studyWrapUpDraft';

const inputClass =
  'mt-2 w-full rounded-2xl border border-hairline bg-canvas-pure p-3 text-sm text-ink';

export function WrapUpStepBar({ step }: { step: WrapUpStep }) {
  return (
    <ol aria-label="Wrap-up steps" className="grid grid-cols-3 gap-2">
      {WRAP_UP_STEPS.map((item) => (
        <li
          key={item.step}
          aria-current={item.step === step ? 'step' : undefined}
          className={clsx(
            'rounded-2xl border px-3 py-2 text-xs font-bold sm:text-sm',
            item.step === step
              ? 'border-ink bg-wash-sky text-ink'
              : item.step < step
                ? 'border-hairline bg-canvas-pure text-ink'
                : 'border-hairline bg-canvas-pure text-slate2',
          )}
        >
          <span className="mr-1">{item.step}</span>
          {item.label}
        </li>
      ))}
    </ol>
  );
}

/** Step 1 (PRD §5.1, §5.4): the count from the server draft and an optional reflection. */
export function LookBackStep({
  draft,
  reflection,
  onReflection,
  storyHref,
  onOpenStory,
}: {
  draft: RolloverDraft;
  reflection: string;
  onReflection: (value: string) => void;
  storyHref: string;
  onOpenStory: () => void;
}) {
  const year = isYearWrapUp(draft);
  return (
    <div className="space-y-5">
      <p className="text-base font-bold text-ink">{unfinishedSentence(draft)}</p>
      <div>
        <label htmlFor="wrap-up-reflection" className="block text-sm font-bold text-ink">
          One thing that helped you this Term?
        </label>
        <textarea
          id="wrap-up-reflection"
          aria-describedby="wrap-up-reflection-count"
          value={reflection}
          maxLength={REFLECTION_MAX}
          rows={3}
          onChange={(event) => onReflection(event.target.value)}
          className={inputClass}
        />
        <p id="wrap-up-reflection-count" className="mt-1 text-xs font-semibold text-slate2">
          Optional · {reflection.length}/{REFLECTION_MAX}
        </p>
      </div>
      {year && (
        <Link
          to={storyHref}
          onClick={onOpenStory}
          className="inline-flex min-h-11 items-center text-sm font-extrabold text-brand-sky underline underline-offset-4"
        >
          Write my year story
        </Link>
      )}
    </div>
  );
}

type Lookup = Map<string, { subject: string | null }>;

function ItemCard({
  kind,
  id,
  title,
  dueOn,
  subject,
  today,
  isNew,
  options,
  value,
  onChange,
}: {
  kind: 'task' | 'assessment';
  id: string;
  title: string;
  dueOn: string | null;
  subject: string | null;
  today: string;
  isNew: boolean;
  options: Array<{ value: string; label: string }>;
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  const overdue = dueOn !== null && dueOn < today;
  return (
    <article className="rounded-3xl border border-hairline bg-canvas-pure p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate2">
        <span>{kind === 'task' ? 'Task' : 'Assessment'}</span>
        {isNew && <span className="rounded-full bg-wash-sunshine px-2 py-0.5 text-ink">New</span>}
      </div>
      <h3 className="mt-1 break-words text-lg font-extrabold text-ink">{title}</h3>
      <p className="mt-1 text-sm text-ink-soft">
        {subject ?? 'No subject'}
        {dueOn ? ` · School due date ${formatShortDate(dueOn)}` : ''}
      </p>
      {overdue && dueOn && (
        <p className="mt-2 text-sm text-ink">
          The school due date ({formatShortDate(dueOn)}) has passed. Check with your teacher if it
          changed.
        </p>
      )}
      <fieldset className="mt-3">
        <legend className="sr-only">What to do with {title}</legend>
        <div className="flex flex-wrap gap-2">
          {options.map((option) => {
            const checked = value === option.value;
            return (
              <label
                key={option.value}
                className={clsx(
                  'flex min-h-11 cursor-pointer items-center gap-2 rounded-full border px-4 text-sm font-bold',
                  checked ? 'border-ink bg-wash-sky text-ink' : 'border-hairline text-ink-soft',
                )}
              >
                <input
                  type="radio"
                  name={`wrap-up-${kind}-${id}`}
                  value={option.value}
                  checked={checked}
                  onChange={() => onChange(option.value)}
                />
                {option.label}
              </label>
            );
          })}
        </div>
      </fieldset>
    </article>
  );
}

/** Step 2 (PRD §5.2): one choice per unfinished item. Tasks have three choices, assessments two. */
export function UnfinishedWorkStep({
  draft,
  state,
  today,
  lookup,
  added,
  removed,
  onTask,
  onAssessment,
  onBringEverything,
  chosen,
}: {
  draft: RolloverDraft;
  state: WrapUpState;
  today: string;
  lookup: Lookup;
  added: string[];
  removed: string[];
  onTask: (id: string, choice: TaskChoice) => void;
  onAssessment: (id: string, choice: AssessmentChoice) => void;
  onBringEverything: () => void;
  chosen: number;
}) {
  const next = nextPeriodLabel(draft);
  const total = draft.tasks.length + draft.assessments.length;
  if (total === 0) return <p className="text-base font-bold text-ink">Nothing left over. Nice.</p>;
  const taskOptions = [
    { value: 'continue', label: `Keep going in ${next}` },
    { value: 'defer', label: 'Save for later' },
    { value: 'archive', label: 'Drop it' },
  ];
  const assessmentOptions = [
    { value: 'continue', label: `Keep in ${next}` },
    { value: 'archive', label: 'Drop it' },
  ];
  return (
    <div className="space-y-4">
      {removed.length > 0 && (
        <p className="rounded-2xl bg-surface p-4 text-sm text-ink">
          No longer here: {removed.join(', ')}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" className="btn-pill-secondary" onClick={onBringEverything}>
          Bring everything to {next}
        </button>
        <p role="status" className="text-sm font-bold text-slate2">
          {chosen} of {total} chosen
        </p>
      </div>
      {draft.tasks.map((task) => (
        <ItemCard
          key={task.id}
          kind="task"
          id={task.id}
          title={task.title}
          dueOn={task.due_on}
          subject={lookup.get(task.id)?.subject ?? null}
          today={today}
          isNew={added.includes(task.id)}
          options={taskOptions}
          value={state.choices.tasks[task.id]}
          onChange={(value) => onTask(task.id, value as TaskChoice)}
        />
      ))}
      {draft.assessments.map((assessment) => (
        <ItemCard
          key={assessment.id}
          kind="assessment"
          id={assessment.id}
          title={assessment.title}
          dueOn={assessment.due_on}
          subject={lookup.get(assessment.id)?.subject ?? null}
          today={today}
          isNew={added.includes(assessment.id)}
          options={assessmentOptions}
          value={state.choices.assessments[assessment.id]}
          onChange={(value) => onAssessment(assessment.id, value as AssessmentChoice)}
        />
      ))}
    </div>
  );
}

/** Step 3 (PRD §5.3, §5.4): subjects, dates and, for a new year, the next Year level. */
export function NextTermStep({
  draft,
  state,
  dateSource,
  editingDates,
  onEditDates,
  onChange,
}: {
  draft: RolloverDraft;
  state: WrapUpState;
  dateSource: string | null;
  editingDates: boolean;
  onEditDates: () => void;
  onChange: (patch: Partial<WrapUpState>) => void;
}) {
  const year = isYearWrapUp(draft);
  const next = year ? 'Term 1' : `Term ${draft.term_number + 1}`;
  const destinationYear = year ? draft.school_year + 1 : draft.school_year;
  const dates =
    state.startsOn && state.endsOn
      ? `${formatShortDate(state.startsOn)} – ${formatShortDate(state.endsOn)}`
      : 'No dates yet';
  return (
    <div className="space-y-6">
      {year && (
        <div className="space-y-3 rounded-3xl bg-surface p-4">
          <p className="text-base font-extrabold text-ink">Next year: {destinationYear}</p>
          <label className="block text-sm font-bold text-ink">
            Year level next year
            <select
              value={state.nextYearLevel}
              onChange={(event) => onChange({ nextYearLevel: Number(event.target.value) })}
              className={inputClass}
            >
              {Array.from(
                { length: MAX_YEAR_LEVEL - MIN_YEAR_LEVEL + 1 },
                (_, i) => MIN_YEAR_LEVEL + i,
              ).map((level) => (
                <option key={level} value={level}>
                  Year {level}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-slate2">
            Changing your Year level does not change your {draft.school_year} work.
          </p>
        </div>
      )}
      <fieldset>
        <legend className="text-base font-extrabold text-ink">
          Subjects in {year ? destinationYear : next}
        </legend>
        {draft.subjects.length === 0 && state.addedSubjectKeys.length === 0 ? (
          <p className="mt-2 text-sm text-ink-soft">
            No subjects yet. Add one below, or add them after you start.
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {draft.subjects.map((subject) => {
              const checked = state.subjectKeys.includes(subject.subject_key);
              return (
                <label
                  key={subject.subject_key}
                  className={clsx(
                    'flex min-h-11 cursor-pointer items-center gap-2 rounded-full border px-4 text-sm font-bold',
                    checked ? 'border-ink bg-wash-mint text-ink' : 'border-hairline text-ink-soft',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onChange({
                        subjectKeys: checked
                          ? state.subjectKeys.filter((key) => key !== subject.subject_key)
                          : [...state.subjectKeys, subject.subject_key],
                      })
                    }
                  />
                  {subject.display_name}
                </label>
              );
            })}
            {state.addedSubjectKeys.map((key) => (
              <span
                key={key}
                className="flex min-h-11 items-center gap-2 rounded-full border border-ink bg-wash-sky pl-4 pr-1 text-sm font-bold text-ink"
              >
                {catalogueSubjectName(key)}
                <span className="rounded-full bg-canvas-pure px-2 py-0.5 text-xs text-slate2">
                  New
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${catalogueSubjectName(key)}`}
                  onClick={() =>
                    onChange({
                      addedSubjectKeys: state.addedSubjectKeys.filter((item) => item !== key),
                    })
                  }
                  className="min-h-11 rounded-full px-3 text-slate2 underline underline-offset-4 hover:text-ink"
                >
                  Remove
                </button>
              </span>
            ))}
          </div>
        )}
        <AddSubjectControl
          draft={draft}
          state={state}
          onAdd={(key) => onChange({ addedSubjectKeys: [...state.addedSubjectKeys, key] })}
        />
      </fieldset>
      <div>
        <h3 className="text-base font-extrabold text-ink">{next} dates</h3>
        <p className="mt-1 text-sm text-ink">{dates}</p>
        {dateSource && <p className="mt-1 text-xs font-semibold text-slate2">{dateSource}</p>}
        {editingDates ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-bold text-ink">
              Starts
              <input
                type="date"
                value={state.startsOn}
                min={`${destinationYear}-01-01`}
                max={`${destinationYear}-12-31`}
                onChange={(event) => onChange({ startsOn: event.target.value })}
                className={inputClass}
              />
            </label>
            <label className="text-sm font-bold text-ink">
              Ends
              <input
                type="date"
                value={state.endsOn}
                min={`${destinationYear}-01-01`}
                max={`${destinationYear}-12-31`}
                onChange={(event) => onChange({ endsOn: event.target.value })}
                className={inputClass}
              />
            </label>
          </div>
        ) : (
          <button type="button" className="btn-pill-ghost mt-3" onClick={onEditDates}>
            Change dates
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * "Add a subject" (PRD §5.3): the Term setup subject catalogue, without the subjects of this Term
 * and those already added. The automatic summary refreshes because the plan changes.
 */
function AddSubjectControl({
  draft,
  state,
  onAdd,
}: {
  draft: RolloverDraft;
  state: WrapUpState;
  onAdd: (subjectKey: string) => void;
}) {
  const [choice, setChoice] = useState('');
  const options = addableSubjects(draft, state);
  const full = nextTermSubjectCount(state) >= MAX_NEXT_TERM_SUBJECTS;
  if (options.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-end gap-2">
        <label className="block min-w-0 flex-1 text-sm font-bold text-ink">
          Add a subject
          <select
            value={choice}
            disabled={full}
            onChange={(event) => setChoice(event.target.value)}
            className={inputClass}
          >
            <option value="">Choose a subject</option>
            {options.map((subject) => (
              <option key={subject.subject_key} value={subject.subject_key}>
                {subject.display_name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn-pill-secondary"
          disabled={!choice || full}
          onClick={() => {
            onAdd(choice);
            setChoice('');
          }}
        >
          Add
        </button>
      </div>
      {full && (
        <p className="mt-2 text-xs font-semibold text-slate2">
          You can have up to {MAX_NEXT_TERM_SUBJECTS} subjects.
        </p>
      )}
    </div>
  );
}
